/* eslint-disable no-console */
import { sortBy } from 'lodash';
import { captureKeyboardShortcutAction } from '@frameio/telemetry';
import {
  type ArithmeticKeys,
  isComposingEvent,
  type LowerAlpha,
  type ModifierKeys,
  type NavigationKeys,
  type NumericKeys,
  type SpecialCharacterKeys,
  type WhitespaceKeys,
} from '@frameio/vapor';
import type { Command } from './types/Command';
import {
  filterActionModifiers,
  getKeyboardShortcutString,
} from './utils/getKeyboardShortcutString';
import { isModifierKey } from './utils/isModifier';
import { normalizeKeyFromEvent } from './utils/normalizeKey';

/**
 * Central registry for commands, which can be invoked by keyboard shortcuts
 * and are presented throughout the UI.
 *
 * 🚧 wip: https://frame.quip.com/5hEZAbQqi8Pj/tech-brief-v4-Keyboard-Shortcuts
 */
export class CommandRegistry {
  /** Enable for helpful console messages. */
  private static _isDebugEnabled = false;

  /** Used for OS-dependent features. */
  private _isApple: boolean;

  /** Used to restrict commands based on panel priming */
  private _primedPanelArea: Command['panel'];

  /** All commands in registration order. */
  private _commandsById = new Map<Command['id'], Command>();

  /** All keyboard shortcuts in registration order. */
  private _idsByShortcut = new Map<
    Command['displayKeys'],
    Array<Command['id']>
  >();

  /** All alternative keyboard shortcuts in registration order. */
  private _idsByAlternativeShortcut = new Map<
    Command['alternativeDisplayKeys'],
    Array<Command['id']>
  >();

  /**
   * Maps command IDs → layout modifiers stripped when the command was activated on keydown.
   * Enables onKeyUp to fire when that modifier is released before the physical key.
   */
  private _commandStrippedModifiers = new Map<Command['id'], Set<string>>();

  /** @todo (ACE-17): automatic usage logging */
  private _stats: Record<
    Command['id'],
    Array<{ didComplete: boolean; didRun: boolean; time: Date }>
  > = {};

  /** Useful for debugging */
  private _debugStats = () => {
    const stats = Object.entries(this._stats).map(([id, statEntry]) => {
      const command = this.get(id);
      return {
        count: statEntry.length,
        displayCategory: command?.displayCategory,
        displayKeys: command?.displayKeys,
        displayLabel: command?.displayLabel,
        handlers: command?.handlers,
        id: command?.id,
      };
    });
    const results = sortBy(stats, 'count').reverse();
    console.group('CommandRegistry Stats');
    console.table(results);
    console.groupEnd();
  };

  /**
   * Global event handler to process input and select a command to execute.
   *
   * @todo (ACE-14): when adding panel focus, consider switching to
   * queue-based execution so that commands can block each other.
   */
  private _onKeyDown = (event: KeyboardEvent) => {
    let normalizedEventKeys: Array<
      | ModifierKeys
      | NavigationKeys
      | WhitespaceKeys
      | LowerAlpha
      | SpecialCharacterKeys
      | NumericKeys
      | ArithmeticKeys
    > = [];

    const hotkeysAllowed =
      event.target instanceof HTMLElement
        ? event.target.getAttribute('data-allow-hotkeys')
        : false;
    // avoid processing while inputting text
    if (
      document.activeElement instanceof HTMLInputElement ||
      document.activeElement instanceof HTMLTextAreaElement ||
      document.activeElement instanceof HTMLAnchorElement ||
      (document.activeElement as any)?.isContentEditable
    ) {
      return;
    }

    const isDialogOpened = Array.from(
      document.querySelectorAll('[role="dialog"]')
    ).some(
      (dialog) =>
        dialog.contains(document.activeElement) &&
        (!dialog.hasAttribute('aria-hidden') ||
          dialog.getAttribute('aria-hidden') === 'false')
    );

    // avoid processing while a modal is open unless shortcuts are allowed (eg: Quicklook)
    if (isDialogOpened && !hotkeysAllowed) {
      return;
    }

    // avoid processing IME event, see:
    // https://developer.mozilla.org/en-US/docs/Web/API/CompositionEvent
    if (isComposingEvent(event)) {
      return;
    }

    // avoid processing autofill events
    if (event.key === undefined) {
      return;
    }

    // avoid processing modifier keys alone
    if (isModifierKey(event.key as any) && event.key !== 'Shift') {
      return;
    }

    if (event.altKey) {
      normalizedEventKeys.push('Alt');
    }
    // Consider the Meta key as the Control key on non-Apple platforms, see:
    // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/metaKey
    if (event.ctrlKey) {
      normalizedEventKeys.push(this._isApple ? 'Control' : 'Meta');
    }
    if (event.metaKey) {
      normalizedEventKeys.push('Meta');
    }
    if (event.shiftKey) {
      normalizedEventKeys.push('Shift');
    }

    // Normalize the key using shared utility (handles uppercase, shifted symbols, alt symbols)
    const key = normalizeKeyFromEvent(event);
    normalizedEventKeys.push(key as any);

    // Snapshot modifiers before stripping so we can track which were layout-synthetic.
    const modifiersBeforeStrip = normalizedEventKeys.filter((k) =>
      isModifierKey(k as any)
    );

    // Drop only the synthetic Shift used to type shifted symbols so
    // modifier shortcuts like Control+Shift+= can still match Control++.
    if (event.shiftKey && (event.key === '?' || event.key === '+')) {
      normalizedEventKeys = normalizedEventKeys.filter(
        (item) => item !== 'Shift'
      );
    }

    // Some keyboard layouts require Alt/Shift to type "\".
    // Treat those modifiers as synthetic so "\" shortcuts stay layout-agnostic.
    if (key === '\\') {
      normalizedEventKeys = normalizedEventKeys.filter(
        (item) => item !== 'Alt' && item !== 'Shift'
      );
    }

    const strippedModifiers = new Set(
      modifiersBeforeStrip.filter((k) => !normalizedEventKeys.includes(k))
    );

    const normalizedEventShortcut = getKeyboardShortcutString(
      normalizedEventKeys,
      this._isApple
    );

    const commandIdsByMainShortcut =
      this._idsByShortcut.get(normalizedEventShortcut) || [];
    const commandIdsByAlternativeShortcut =
      this._idsByAlternativeShortcut.get(normalizedEventShortcut) || [];

    const allCommandIds = [
      ...commandIdsByAlternativeShortcut,
      ...commandIdsByMainShortcut,
    ];
    const runsFirst = (id: Command['id']) =>
      Boolean(this._commandsById.get(id)?.runsFirst);
    const commandIds = [
      ...allCommandIds.filter(runsFirst),
      ...allCommandIds.filter((id) => !runsFirst(id)),
    ];

    // Allow native clipboard behavior when text is selected
    const hasTextSelection =
      (window.getSelection()?.toString().length ?? 0) > 0;
    const hasClipboardCommand = commandIds.some(
      (id) =>
        id === 'finder.copySelection' ||
        id === 'finder.cutSelection' ||
        id === 'finder.pasteClipboard'
    );

    if (hasTextSelection && hasClipboardCommand) {
      return;
    }

    this.execute(commandIds, event);

    if (
      event.type === 'keydown' &&
      commandIds.length > 0 &&
      strippedModifiers.size > 0
    ) {
      commandIds.forEach((id) =>
        this._commandStrippedModifiers.set(id, strippedModifiers)
      );
    } else if (event.type === 'keyup' && commandIds.length > 0) {
      // Physical key released while modifier still held (event.key still matches shortcut).
      // Clear tracking so the subsequent modifier keyup doesn't double-fire.
      commandIds.forEach((id) => this._commandStrippedModifiers.delete(id));
    }

    if (CommandRegistry._isDebugEnabled) {
      console.log(
        `${normalizedEventShortcut} (${event.key} ${
          event.code
        }): ${commandIds.join(', ')}`
      );
    }
  };

  private _onKeyUp = (event: KeyboardEvent) => {
    // If a layout modifier was released before the physical key, check if it was
    // stripped during command activation on keydown, and fire onKeyUp handlers.
    if (isModifierKey(event.key as any) && event.key !== 'Shift') {
      const commandsToFire: Array<Command['id']> = [];
      const commandsToDelete: Array<Command['id']> = [];
      this._commandStrippedModifiers.forEach((strippedMods, commandId) => {
        if (strippedMods.has(event.key)) {
          commandsToFire.push(commandId);
          commandsToDelete.push(commandId);
        }
      });
      if (commandsToFire.length > 0) {
        commandsToDelete.forEach((id) =>
          this._commandStrippedModifiers.delete(id)
        );
        this.execute(commandsToFire, event);
      }
      return;
    }
    this._onKeyDown(event);
  };

  private _onBlur = () => {
    this._commandStrippedModifiers.clear();
  };

  /** Whether the command registry is mounted. If the registry is not mounted,
   * it will not process any commands or allow handlers to be assigned.
   * */
  private _mounted = false;

  constructor(options: { isApple?: boolean } = {}) {
    this._isApple = Boolean(options?.isApple);
    this._mounted = true;
    if (CommandRegistry._isDebugEnabled) {
      this.register({
        displayCategory: 'Locate',
        displayLabel: 'DEBUG: logs stats to console',
        handlers: [{ onKeyUp: this._debugStats }],
        id: 'debug',
        keys: ['6'],
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this._onKeyDown);
      window.addEventListener('keyup', this._onKeyUp);
      window.addEventListener('blur', this._onBlur);
    }
  }

  /**
   * Clears handlers without clearing stats.
   */
  unmount() {
    this._mounted = false;
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
    this._commandStrippedModifiers.clear();
    const origStats = { ...this._stats };
    this._commandsById.forEach((__command, id) => this.unregister(id));
    this._stats = origStats;
  }

  /**
   * Execute each handler in the chain.
   * - Each handler receives the original event.
   * - If a handler is asynchronous then the next handler waits.
   * - If a handler returns `false` then the chain is stopped early.
   * - Usage is always logged after handlers ran.
   * - Event propagation is always stopped.
   *
   * @todo (ACE-17): automatic usage logging
   */
  async execute(
    ids: ReadonlyArray<Command['id']>,
    event: KeyboardEvent
  ): Promise<void> {
    let __didRun = false;
    let __didComplete = false;

    const enabledCommands = ids
      .map((id) => this._commandsById.get(id))
      .filter((command) => command && !command?.disabled)
      .filter(
        (command) =>
          !command?.panel ||
          !this._primedPanelArea ||
          command?.panel === this._primedPanelArea
      );

    const enabledCommandHandlers = enabledCommands
      .map((command) => command?.handlers)
      .flat(1);

    if (!enabledCommandHandlers.length) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    __didRun = true;

    for (const command of enabledCommands) {
      const hasKeyDown = command?.handlers?.some((h) => h?.onKeyDown);
      const hasKeyUp = command?.handlers?.some((h) => h?.onKeyUp);
      // Report once per press: on keydown, or on keyup for keyup-only commands.
      const shouldReport =
        event.type === 'keydown' ? hasKeyDown : !hasKeyDown && hasKeyUp;

      if (command && shouldReport) {
        captureKeyboardShortcutAction(command, event);
      }
    }

    for (const handler of enabledCommandHandlers) {
      let action;

      if (handler) {
        action = event.type === 'keyup' ? handler?.onKeyUp : handler.onKeyDown;
      }

      if (action) {
        const result = await action(event); // eslint-disable-line no-await-in-loop
        __didComplete = true;
        if (result === false) {
          __didComplete = false;
          break;
        }
      }
    }
  }

  get(id: Command['id']) {
    return this._commandsById.get(id) as Readonly<Command>;
  }

  getAll() {
    return this._commandsById as Readonly<typeof this._commandsById>;
  }

  getShortcuts() {
    return this._idsByShortcut as Readonly<typeof this._idsByShortcut>;
  }

  prime(panel: Command['panel']) {
    this._primedPanelArea = panel;
  }

  /**
   * Re-index shortcuts for the given OS, maintaining registration order
   */
  setOs(isApple: boolean) {
    if (this._isApple === isApple) {
      return;
    }

    this._isApple = isApple;

    this._commandsById.forEach((command, id) => {
      if (command?.displayKeys && command?.keys) {
        const prevShortcut = `${command.displayKeys}`;
        const nextShortcut = getKeyboardShortcutString(command.keys, isApple);
        command.displayKeys = nextShortcut; // eslint-disable-line no-param-reassign
        this._commandsById.set(id, command);
        const prev = this._idsByShortcut.get(prevShortcut) || [];
        this._idsByShortcut.set(nextShortcut, prev);
      }
    });
  }

  /**
   * Disable a command without unregistering it or removing a handler.
   */
  setDisabled(id: Command['id'], disabled: boolean) {
    const command = this._commandsById.get(id);

    if (!command) {
      throw new Error(`no command registered with id ${id}`);
      return;
    }

    this._commandsById.set(id, { ...command, disabled });
  }

  /**
   * Add a handler to the chain for an existing command.
   */
  assignHandler(
    id: Command['id'],
    handler: NonNullable<Command['handlers']>[number]
  ) {
    if (!this._mounted) {
      console.warn(
        'CommandRegistry - tried to assign handler to command while not mounted'
      );
      return;
    }

    const command = this._commandsById.get(id);

    if (!command) {
      throw new Error(`no command registered with id ${id}`);
      return;
    }

    const nextCommand = { ...command };
    nextCommand.handlers?.unshift(handler);

    this._commandsById.set(id, nextCommand);
  }

  /**
   * Remove a specific command handler by function identity.
   */
  removeHandler(
    id: Command['id'],
    handler: NonNullable<Command['handlers']>[number]
  ) {
    const command = this._commandsById.get(id);

    if (!command || !command.handlers) {
      return;
    }

    // Compare handlers by their function references, not object identity
    const commandHandlerIndex = command?.handlers?.findIndex(
      (h) =>
        h?.onKeyDown === handler?.onKeyDown && h?.onKeyUp === handler?.onKeyUp
    );

    if (commandHandlerIndex > -1) {
      const nextCommand = { ...command };
      // Create a new array to avoid mutating the original
      nextCommand.handlers = [
        ...command.handlers.slice(0, commandHandlerIndex),
        ...command.handlers.slice(commandHandlerIndex + 1),
      ];
      this._commandsById.set(id, nextCommand);
    }
  }

  /**
   * Register a command. Commands must be registered before handler assignment.
   */
  register(command: Command, index: number = 0) {
    if (!this._mounted) {
      console.warn(
        'CommandRegistry - tried to register command while not mounted'
      );
      return;
    }

    if (!command?.id) {
      throw new Error('command.id is required');
      return;
    }

    if (this._commandsById.has(command.id)) {
      throw new Error(`already registered command with id ${command.id}`);
      return;
    }

    const shortcut = command?.keys
      ? getKeyboardShortcutString(command?.keys, this._isApple)
      : '';
    const alternativeShortcut = command?.alternativeKeys
      ? getKeyboardShortcutString(command?.alternativeKeys, this._isApple)
      : '';
    const actions = command?.actionModifiers?.length
      ? filterActionModifiers(command?.actionModifiers)
      : null;

    const fullCommand = { ...command, handlers: command.handlers || [], index };

    if (command.keys) {
      fullCommand.displayKeys = shortcut;
      fullCommand.actionModifiers = actions;
      fullCommand.alternativeDisplayKeys = alternativeShortcut;
      const prev = this._idsByShortcut.get(shortcut) || [];
      this._idsByShortcut.set(shortcut, [...prev, command.id].filter(Boolean));

      if (alternativeShortcut) {
        const altPrev =
          this._idsByAlternativeShortcut.get(alternativeShortcut) || [];
        this._idsByAlternativeShortcut.set(
          alternativeShortcut,
          [...altPrev, command.id].filter(Boolean)
        );
      }
    }

    this._commandsById.set(command.id, fullCommand);
    this._stats[command.id] = this._stats[command.id] || [];
  }

  /**
   * Unregister a command.
   */
  unregister(id: Command['id']) {
    const command = this._commandsById.get(id);
    if (command) {
      this._stats[command.id] = [];
      this._commandsById.delete(command.id);
      this._commandStrippedModifiers.delete(command.id);
    }
    if (command && command.displayKeys) {
      const prev = this._idsByShortcut.get(command.displayKeys);
      this._idsByShortcut.set(
        command.displayKeys,
        (prev || []).filter((commandId) => commandId !== id)
      );

      // Fix: use alternativeDisplayKeys as the key, not id
      if (command.alternativeDisplayKeys) {
        const altPrev = this._idsByAlternativeShortcut.get(
          command.alternativeDisplayKeys
        );
        this._idsByAlternativeShortcut.set(
          command.alternativeDisplayKeys,
          (altPrev || []).filter((commandId) => commandId !== id)
        );
      }
    }
  }
}
